import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTelegram } from '../context/TelegramContext';
import { useHaptic } from '../hooks/useHaptic';
import axios from 'axios';
import { CreditCard, Lock, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import './ClickPaymentPage.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

function ClickPaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { webApp } = useTelegram();
  const haptic = useHaptic();
  
  const { planType, planName, amount, transactionId } = location.state || {};
  
  // Шаги: 'card' -> 'sms' -> 'processing' -> 'success' / 'error'
  const [step, setStep] = useState('card');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Данные карты
  const [cardNumber, setCardNumber] = useState('');
  const [expireDate, setExpireDate] = useState('');
  const [cardToken, setCardToken] = useState(null);
  const [maskedPhone, setMaskedPhone] = useState('');
  
  // SMS код
  const [smsCode, setSmsCode] = useState('');
  
  // Refs для автофокуса
  const smsInputRef = useRef(null);

  useEffect(() => {
    if (!planType || !amount) {
      setError('Данные платежа не найдены');
      setStep('error');
    }
  }, [planType, amount]);

  // Обработка кнопки "Назад" в Telegram
  useEffect(() => {
    if (webApp) {
      webApp.BackButton.show();
      webApp.BackButton.onClick(handleBack);

      return () => {
        webApp.BackButton.offClick(handleBack);
        webApp.BackButton.hide();
      };
    }
  }, [webApp, step]);

  const handleBack = () => {
    haptic.impactOccurred?.('light');
    
    if (step === 'sms') {
      setStep('card');
      setSmsCode('');
      setError(null);
    } else if (step === 'card') {
      if (webApp) {
        webApp.showConfirm('Отменить оплату?', (confirmed) => {
          if (confirmed) {
            navigate('/upgrade', { replace: true });
          }
        });
      } else {
        if (window.confirm('Отменить оплату?')) {
          navigate('/upgrade', { replace: true });
        }
      }
    } else {
      navigate('/upgrade', { replace: true });
    }
  };

  // Форматирование номера карты (8600 1234 5678 9012)
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.slice(0, 16);
    const groups = limited.match(/.{1,4}/g);
    return groups ? groups.join(' ') : '';
  };

  // Форматирование срока действия (MM/YY)
  const formatExpireDate = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.slice(0, 4);
    if (limited.length >= 2) {
      return limited.slice(0, 2) + '/' + limited.slice(2);
    }
    return limited;
  };

  // Шаг 1: Запрос токена карты (отправка SMS)
  const handleRequestToken = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    haptic.impactOccurred?.('medium');

    try {
      const initData = window.Telegram?.WebApp?.initData;
      
      if (!initData) {
        throw new Error('Telegram WebApp не инициализирован');
      }

      const response = await axios.post(
        `${API_URL}/payments/click/card/token/request`,
        {
          cardNumber: cardNumber.replace(/\s/g, ''),
          expireDate: expireDate.replace('/', ''),
          temporary: true, // Временный токен для разовой оплаты
        },
        {
          headers: {
            'x-telegram-init-data': initData,
          },
        }
      );

      if (response.data.success) {
        setCardToken(response.data.cardToken);
        setMaskedPhone(response.data.phoneNumber);
        setStep('sms');
        haptic.notificationOccurred?.('success');
        
        // Автофокус на поле SMS
        setTimeout(() => {
          smsInputRef.current?.focus();
        }, 100);
      } else {
        throw new Error(response.data.error || 'Ошибка запроса токена');
      }
    } catch (err) {
      console.error('Token request error:', err);
      setError(err.response?.data?.error || err.message || 'Ошибка отправки SMS');
      haptic.notificationOccurred?.('error');
    } finally {
      setLoading(false);
    }
  };

  // Шаг 2: Подтверждение SMS и оплата
  const handleVerifyAndPay = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setStep('processing');
    haptic.impactOccurred?.('medium');

    try {
      const initData = window.Telegram?.WebApp?.initData;
      
      if (!initData) {
        throw new Error('Telegram WebApp не инициализирован');
      }

      // Сначала подтверждаем токен
      const verifyResponse = await axios.post(
        `${API_URL}/payments/click/card/token/verify`,
        {
          cardToken: cardToken,
          smsCode: smsCode,
        },
        {
          headers: {
            'x-telegram-init-data': initData,
          },
        }
      );

      if (!verifyResponse.data.success) {
        throw new Error(verifyResponse.data.error || 'Неверный SMS код');
      }

      // Теперь выполняем оплату
      const paymentResponse = await axios.post(
        `${API_URL}/payments/click/card/payment`,
        {
          cardToken: verifyResponse.data.cardToken,
          planType: planType,
        },
        {
          headers: {
            'x-telegram-init-data': initData,
          },
        }
      );

      if (paymentResponse.data.success) {
        if (paymentResponse.data.status === 'completed') {
          setStep('success');
          haptic.notificationOccurred?.('success');
        } else {
          // Платеж в обработке, polling
          startPaymentPolling(paymentResponse.data.transaction.transaction_id, initData);
        }
      } else {
        throw new Error(paymentResponse.data.error || 'Ошибка оплаты');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.error || err.message || 'Ошибка оплаты');
      setStep('error');
      haptic.notificationOccurred?.('error');
    }
  };

  // Polling статуса платежа
  const startPaymentPolling = (txId, initData) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(
          `${API_URL}/payments/status/${txId}`,
          {
            headers: {
              'x-telegram-init-data': initData,
            },
          }
        );

        if (response.data.status === 'completed') {
          clearInterval(pollInterval);
          setStep('success');
          haptic.notificationOccurred?.('success');
        } else if (response.data.status === 'failed' || response.data.status === 'cancelled') {
          clearInterval(pollInterval);
          setError('Платеж не прошёл');
          setStep('error');
          haptic.notificationOccurred?.('error');
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000);

    // Остановить через 5 минут
    setTimeout(() => {
      clearInterval(pollInterval);
      if (step === 'processing') {
        setError('Превышено время ожидания');
        setStep('error');
      }
    }, 300000);
  };

  // Успешное завершение
  const handleSuccess = () => {
    haptic.impactOccurred?.('medium');
    navigate('/upgrade', { replace: true });
    window.location.reload();
  };

  // Рендер формы карты
  const renderCardForm = () => (
    <form onSubmit={handleRequestToken} className="click-payment-form">
      <div className="form-header">
        <CreditCard size={48} className="form-icon" />
        <h2>Оплата картой</h2>
        <p className="form-subtitle">
          {planName} — {amount?.toLocaleString()} сум
        </p>
      </div>

      <div className="form-group">
        <label htmlFor="cardNumber">Номер карты</label>
        <input
          type="text"
          id="cardNumber"
          inputMode="numeric"
          placeholder="8600 0000 0000 0000"
          value={cardNumber}
          onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
          maxLength={19}
          required
          autoComplete="cc-number"
        />
        <span className="card-hint">UzCard или HUMO</span>
      </div>

      <div className="form-group">
        <label htmlFor="expireDate">Срок действия</label>
        <input
          type="text"
          id="expireDate"
          inputMode="numeric"
          placeholder="MM/YY"
          value={expireDate}
          onChange={(e) => setExpireDate(formatExpireDate(e.target.value))}
          maxLength={5}
          required
          autoComplete="cc-exp"
        />
      </div>

      {error && (
        <div className="form-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        className="submit-button"
        disabled={loading || cardNumber.replace(/\s/g, '').length < 16 || expireDate.length < 5}
      >
        {loading ? (
          <span className="button-loading">
            <span className="loader-small"></span>
            Отправка...
          </span>
        ) : (
          <>
            <Lock size={18} />
            Продолжить
          </>
        )}
      </button>

      <div className="security-note">
        <Lock size={14} />
        <span>Безопасная оплата через Click</span>
      </div>
    </form>
  );

  // Рендер формы SMS
  const renderSmsForm = () => (
    <form onSubmit={handleVerifyAndPay} className="click-payment-form">
      <div className="form-header">
        <div className="sms-icon">📱</div>
        <h2>Подтверждение</h2>
        <p className="form-subtitle">
          Код отправлен на номер {maskedPhone}
        </p>
      </div>

      <div className="form-group">
        <label htmlFor="smsCode">Код из SMS</label>
        <input
          ref={smsInputRef}
          type="text"
          id="smsCode"
          inputMode="numeric"
          placeholder="000000"
          value={smsCode}
          onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          maxLength={6}
          required
          autoComplete="one-time-code"
          className="sms-input"
        />
      </div>

      {error && (
        <div className="form-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        className="submit-button"
        disabled={loading || smsCode.length < 4}
      >
        {loading ? (
          <span className="button-loading">
            <span className="loader-small"></span>
            Оплата...
          </span>
        ) : (
          <>
            Оплатить {amount?.toLocaleString()} сум
          </>
        )}
      </button>

      <button
        type="button"
        className="back-link"
        onClick={() => {
          setStep('card');
          setSmsCode('');
          setError(null);
        }}
      >
        <ArrowLeft size={16} />
        Изменить карту
      </button>
    </form>
  );

  // Рендер обработки
  const renderProcessing = () => (
    <div className="click-payment-status">
      <div className="status-icon processing">
        <span className="loader-large"></span>
      </div>
      <h2>Обработка платежа</h2>
      <p>Пожалуйста, подождите...</p>
    </div>
  );

  // Рендер успеха
  const renderSuccess = () => (
    <div className="click-payment-status">
      <div className="status-icon success">
        <CheckCircle size={64} />
      </div>
      <h2>Оплата успешна!</h2>
      <p>Подписка активирована</p>
      <button className="submit-button" onClick={handleSuccess}>
        Продолжить
      </button>
    </div>
  );

  // Рендер ошибки
  const renderError = () => (
    <div className="click-payment-status">
      <div className="status-icon error">
        <AlertCircle size={64} />
      </div>
      <h2>Ошибка</h2>
      <p>{error || 'Произошла ошибка при оплате'}</p>
      <button 
        className="submit-button" 
        onClick={() => {
          setStep('card');
          setError(null);
          setSmsCode('');
        }}
      >
        Попробовать снова
      </button>
      <button 
        className="back-link" 
        onClick={() => navigate('/upgrade', { replace: true })}
      >
        Вернуться назад
      </button>
    </div>
  );

  return (
    <div className="click-payment-page">
      <div className="click-payment-container">
        {step === 'card' && renderCardForm()}
        {step === 'sms' && renderSmsForm()}
        {step === 'processing' && renderProcessing()}
        {step === 'success' && renderSuccess()}
        {step === 'error' && renderError()}
      </div>
    </div>
  );
}

export default ClickPaymentPage;
